import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { RefreshCw, Trash2, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export function MetaAdsOAuthSection() {
  const { authAxios } = useAuth();
  const [oauthConnected, setOauthConnected] = useState(false);
  const [oauthTokenExpiry, setOauthTokenExpiry] = useState(null);
  const [authorizing, setAuthorizing] = useState(false);
  const pollingIntervalRef = useRef(null);
  const oauthWindowRef = useRef(null);

  // Load OAuth status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await authAxios.get('/marketing/meta-ads/oauth/status');
        setOauthConnected(res.data.connected);
        setOauthTokenExpiry(res.data.token_expires_at);
      } catch (err) {
        // 404 = not connected
        if (err.response?.status !== 404) {
          console.warn('OAuth status check failed:', err);
        }
      }
    };
    checkStatus();
  }, [authAxios]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const startOAuthFlow = async () => {
    setAuthorizing(true);
    try {
      const res = await authAxios.post('/marketing/meta-ads/oauth/authorize');
      const { authorization_url } = res.data;

      const popup = window.open(
        authorization_url,
        'meta_oauth_popup',
        'width=500,height=600,left=100,top=100'
      );

      if (!popup) {
        toast.error('Popup bloqueado. Permita popups neste site.');
        setAuthorizing(false);
        return;
      }

      oauthWindowRef.current = popup;

      let pollCount = 0;
      const maxPolls = 120;

      pollingIntervalRef.current = setInterval(async () => {
        pollCount++;

        if (popup.closed) {
          clearInterval(pollingIntervalRef.current);
          setAuthorizing(false);
          toast.info('OAuth popup fechado');
          return;
        }

        try {
          const statusRes = await authAxios.get('/marketing/meta-ads/oauth/status');
          if (statusRes.data.connected) {
            clearInterval(pollingIntervalRef.current);
            popup.close();
            oauthWindowRef.current = null;
            setOauthConnected(true);
            setOauthTokenExpiry(statusRes.data.token_expires_at);
            setAuthorizing(false);
            toast.success('Meta Business conectado com sucesso!');
          }
        } catch (err) {
          console.warn('Polling:', err);
        }

        if (pollCount > maxPolls) {
          clearInterval(pollingIntervalRef.current);
          popup.close();
          setAuthorizing(false);
          toast.error('Timeout na autenticação OAuth');
        }
      }, 500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Erro ao iniciar OAuth');
      setAuthorizing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Desconectar Meta Business?')) {
      return;
    }

    try {
      await authAxios.delete('/marketing/meta-ads/disconnect');
      setOauthConnected(false);
      setOauthTokenExpiry(null);
      toast.success('Meta Business desconectado');
    } catch (err) {
      toast.error('Erro ao desconectar');
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
      <h3 className="font-semibold text-sm text-blue-900 mb-3">Conexão OAuth (v2 — Recomendado)</h3>
      {oauthConnected ? (
        <div className="space-y-2">
          <p className="text-sm text-blue-800">✅ Meta Business conectado com segurança</p>
          {oauthTokenExpiry && (
            <p className="text-xs text-blue-700">
              Válido até: {new Date(oauthTokenExpiry).toLocaleString('pt-BR')}
            </p>
          )}
          <Button
            onClick={handleDisconnect}
            disabled={authorizing}
            variant="ghost"
            size="sm"
            className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 w-full mt-2"
          >
            <Trash2 className="w-4 h-4" />
            Desconectar Meta Business
          </Button>
        </div>
      ) : (
        <Button
          onClick={startOAuthFlow}
          disabled={authorizing}
          size="sm"
          className="gap-2 w-full bg-blue-600 hover:bg-blue-700"
        >
          {authorizing ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Conectando...
            </>
          ) : (
            <>
              <LogIn className="w-4 h-4" />
              Conectar Meta Business
            </>
          )}
        </Button>
      )}
    </div>
  );
}
